import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import Lightbox from "../Lightbox";
import { absUrl, shareLink, embedSrcFrom } from "./media";
import { renderInline } from "./StoryChapterBlock";

// Single / album release centerpiece: cover art, meta, embedded player, buy + share, platform pills, cause note.
const ReleaseSpotlightBlock = ({ block, festival }) => {
  const cfg = block.config || {};
  const [zoom, setZoom] = useState(null);
  const [open, setOpen] = useState(!cfg.compact_default);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!cfg.release_at) return undefined;
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, [cfg.release_at]);
  const releaseAt = cfg.release_at ? Date.parse(cfg.release_at) : NaN;
  const isOut = Number.isFinite(releaseAt) && now >= releaseAt;
  const eyebrow = isOut ? (cfg.out_now_eyebrow || "Out now") : (cfg.eyebrow || "New single");
  const metaLine = isOut && cfg.out_now_meta ? cfg.out_now_meta : [cfg.release_date, cfg.format].filter(Boolean).join(" · ");
  const banner = isOut && cfg.out_now_banner && (
    <div className="relative border-b border-white/15" style={{ background: `linear-gradient(90deg, ${cfg.accent || "#F2CC8F"}, #E76F51 60%, #2E5C55)` }} data-testid="release-out-now-banner">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center gap-3 text-[#0A1626] font-bold">
        <Icons.PartyPopper className="h-5 w-5 shrink-0 animate-bounce" />
        <span className="text-sm md:text-base leading-snug [&_strong]:font-extrabold">{renderInline(cfg.out_now_banner, "#0A1626")}</span>
      </div>
    </div>
  );
  const accent = cfg.accent || "#F2CC8F";
  const cover = cfg.cover ? absUrl(cfg.cover) : "";
  const embedSrc = embedSrcFrom(cfg.embed_url);
  const links = (cfg.links || []).filter((l) => l.url);
  const anchor = cfg.anchor || `release-${block.block_id}`;
  const share = () => shareLink({
    title: `${cfg.title || block.title} — ${cfg.artist || festival?.title || ""}`.trim(),
    text: cfg.share_text || cfg.blurb || "",
    url: `${window.location.origin}${window.location.pathname}`,
  });
  const toggle = cfg.compact_default ? (
    <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 hover:bg-white/20 px-5 py-2.5 text-sm font-semibold transition-colors" data-testid="release-toggle">
      <Icons.ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Show less" : (cfg.more_label || "Cover art, release details, buy & share")}
    </button>
  ) : null;
  const embedEl = embedSrc && (
    <div className="mt-7 rounded-2xl overflow-hidden ring-1 ring-white/15 bg-white/5 max-w-2xl" data-testid="release-embed">
      <iframe src={embedSrc} title={`${cfg.title || "Release"} player`} className="w-full block" style={{ height: Number(cfg.embed_height) || 120, border: 0 }} allow="autoplay; clipboard-write; encrypted-media" seamless />
    </div>
  );
  const [aboutOpen, setAboutOpen] = useState(false);
  const aboutParas = (cfg.about_body || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const aboutFacts = (cfg.about_facts || []).filter((f) => f.text);
  const aboutEl = cfg.about_label && (aboutParas.length > 0 || aboutFacts.length > 0) && (
    <div className="mt-6 max-w-3xl rounded-2xl border border-white/15 bg-white/5" data-testid="release-about">
      <button type="button" onClick={() => setAboutOpen(!aboutOpen)} aria-expanded={aboutOpen} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left" data-testid="release-about-toggle">
        <span className="font-['Playfair_Display'] text-xl md:text-2xl font-semibold">{cfg.about_label}</span>
        <Icons.ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${aboutOpen ? "rotate-180" : ""}`} style={{ color: accent }} />
      </button>
      {aboutOpen && (
        <div className="px-5 pb-6 grid md:grid-cols-[1fr_260px] gap-6" data-testid="release-about-body">
          <div className="space-y-4 text-base text-white/80 leading-relaxed [&_strong]:text-white">
            {aboutParas.map((p, i) => <p key={i}>{renderInline(p, accent)}</p>)}
            {cfg.about_quote && (
              <figure className="border-l-4 pl-4 my-4" style={{ borderColor: accent }}>
                <blockquote className="font-['Playfair_Display'] text-lg md:text-xl italic text-white leading-snug">&ldquo;{cfg.about_quote}&rdquo;</blockquote>
                {cfg.about_quote_by && <figcaption className="mt-2 text-xs uppercase tracking-widest text-white/60">— {cfg.about_quote_by}</figcaption>}
              </figure>
            )}
          </div>
          {aboutFacts.length > 0 && (
            <aside className="rounded-xl border border-white/15 bg-black/20 p-4 self-start" data-testid="release-about-facts">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/60 mb-3">{cfg.about_facts_label || "Release facts"}</div>
              <ul className="space-y-3">
                {aboutFacts.map((f, i) => (
                  <li key={i} className="text-sm leading-snug border-l-2 pl-3" style={{ borderColor: accent }}>
                    {f.when && <div className="text-[10px] font-bold uppercase tracking-widest text-white/50">{f.when}</div>}
                    <div className="text-white/80 [&_strong]:text-white">{renderInline(f.text, accent)}</div>
                    {f.link && <a href={f.link} target={/^https?:/i.test(f.link) ? "_blank" : undefined} rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold mt-1 underline underline-offset-2" style={{ color: accent }}>Source <Icons.ExternalLink className="h-3 w-3" /></a>}
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </div>
      )}
    </div>
  );
  return (
    <section id={anchor} className="relative text-white scroll-mt-24" style={{ background: cfg.background || "#0A1626" }} data-testid="fest-release">
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ background: cover ? `url(${cover}) center/cover` : "none", filter: "blur(40px)" }} />
      {banner}
      {!open ? (
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="release-compact">
          <div className="inline-flex items-center gap-2 uppercase tracking-[0.3em] text-xs mb-3" style={{ color: accent }}>
            {isOut ? <Icons.PartyPopper className="h-4 w-4" /> : <Icons.Disc3 className="h-4 w-4" />} {eyebrow}
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold leading-tight" data-testid="release-title">
            {cfg.title || block.title}{cfg.artist && <span className="text-white/70 font-normal"> — {cfg.artist}</span>}
          </h2>
          {cfg.dedication && <p className="mt-5 font-['Playfair_Display'] italic text-lg md:text-xl text-white/90 border-l-2 pl-4 max-w-2xl" style={{ borderColor: accent }} data-testid="release-dedication">{cfg.dedication}</p>}
          {cfg.blurb && <p className="mt-4 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl whitespace-pre-line">{renderInline(cfg.blurb, accent)}</p>}
          {embedEl}
          {aboutEl}
          <div className="mt-6 flex flex-wrap gap-3">{toggle}</div>
        </div>
      ) : (
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20 grid lg:grid-cols-[minmax(0,420px)_1fr] gap-10 lg:gap-16 items-start">
        {cover && (
          <button type="button" onClick={() => setZoom(cover)} className="group relative w-full max-w-md mx-auto lg:mx-0 cursor-zoom-in" aria-label="Enlarge cover art" data-testid="release-cover">
            <img src={cover} alt={`${cfg.title || ""} cover art`} className="w-full aspect-square object-cover rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] ring-1 ring-white/10 transition-transform duration-500 group-hover:-translate-y-1 group-hover:rotate-[-0.5deg]" />
            <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] font-semibold px-2 py-1"><Icons.Maximize2 className="h-3 w-3" /> Enlarge</span>
          </button>
        )}
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 uppercase tracking-[0.3em] text-xs mb-4" style={{ color: accent }}>
            {isOut ? <Icons.PartyPopper className="h-4 w-4" /> : <Icons.Disc3 className="h-4 w-4" />} {eyebrow}
          </div>
          <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05]" data-testid="release-title">{cfg.title || block.title}</h2>
          {cfg.artist && <p className="text-xl md:text-2xl text-white/85 mt-2">{cfg.artist}</p>}
          {metaLine && (
            <p className="text-sm uppercase tracking-widest mt-4" style={{ color: accent }} data-testid="release-meta">{metaLine}</p>
          )}
          {cfg.dedication && <p className="mt-5 font-['Playfair_Display'] italic text-lg md:text-xl text-white/90 border-l-2 pl-4" style={{ borderColor: accent }} data-testid="release-dedication">{cfg.dedication}</p>}
          {cfg.blurb && <p className="mt-5 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl whitespace-pre-line">{renderInline(cfg.blurb, accent)}</p>}
          {embedEl}
          <div className="mt-7 flex flex-wrap gap-3" data-testid="release-actions">
            {cfg.buy_url && (
              <a href={cfg.buy_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold shadow-lg transition-transform hover:-translate-y-0.5" style={{ backgroundColor: accent, color: "#0A1626" }} data-testid="release-buy">
                <Icons.ShoppingBag className="h-4 w-4" /> {cfg.buy_label || "Buy / stream"}
              </a>
            )}
            {cfg.show_share !== false && (
              <button type="button" onClick={share} className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-semibold border border-white/40 bg-white/10 hover:bg-white/20 transition-colors" data-testid="release-share">
                <Icons.Share2 className="h-4 w-4" /> Share this release
              </button>
            )}
            {toggle}
          </div>
          {links.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="release-links">
              {links.map((l, i) => {
                const Icon = Icons[l.icon] || Icons.Link;
                return (
                  <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 hover:bg-white/15 px-4 py-2 text-sm font-semibold transition-colors" data-testid={`release-link-${i}`}>
                    <Icon className="h-4 w-4" style={{ color: accent }} /> {l.label}
                  </a>
                );
              })}
            </div>
          )}
          {cfg.cause_note && (
            <div className="mt-8 flex gap-4 rounded-2xl border p-5 max-w-2xl" style={{ borderColor: `${accent}55`, background: `${accent}12` }} data-testid="release-cause">
              <Icons.HeartHandshake className="h-7 w-7 shrink-0" style={{ color: accent }} />
              <div>
                {cfg.cause_title && <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: accent }}>{cfg.cause_title}</div>}
                <p className="text-sm md:text-base text-white/85 leading-relaxed whitespace-pre-line">{renderInline(cfg.cause_note, accent)}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      )}
      <Lightbox src={zoom} onClose={() => setZoom(null)} />
    </section>
  );
};

export default ReleaseSpotlightBlock;
